/**
 * CompetitorsPage — `/competitors`. Конкурентная разведка через SpyWords.
 *
 * Снапшот хранится в БД (суточный cron) + кнопка «Обновить» (живой запрос).
 * Блоки: таблица расхода/ключей/трафика · тренд расхода · тексты объявлений
 * (для отстройки) · поиск ключей замещения (спрос/CPC/кто бидует).
 */

import { useMemo, useState } from 'react';
import { usePageHeader } from '@/components/layout/pageHeader';
import { BaseChart } from '@/components/charts/BaseChart';
import { Button, Badge, LoadingBlock, ErrorBlock } from '@/shared/ui';
import { chartTokens, baseChart, axisStyle } from '@/lib/echarts';
import {
  useCompetitors,
  useRefreshCompetitors,
  useKeywordLookup,
  type CompetitorEntry,
} from '@/hooks/api/useCompetitors';
import type { EChartsOption } from 'echarts';

function rub(v: number | null | undefined): string {
  if (v == null) return '—';
  return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(v);
}
function num(v: number | null | undefined): string {
  if (v == null) return '—';
  return new Intl.NumberFormat('ru-RU').format(v);
}
function dt(iso: string | null | undefined): string {
  if (!iso) return 'нет снапшота';
  const d = new Date(iso);
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const PALETTE = ['#2563EB', '#16A34A', '#DC2626', '#1D4ED8', '#9333EA', '#0891B2', '#CA8A04', '#DB2777', '#475569', '#15803D'];

export function CompetitorsPage() {
  usePageHeader({ title: 'Конкуренты', subtitle: 'Разведка SpyWords — расход, объявления, ключи замещения' });

  const { data, isLoading, isError, error, refetch } = useCompetitors();
  const refresh = useRefreshCompetitors();

  const competitors = data?.competitors ?? [];
  const domainComps = competitors.filter((c) => !c.shared);
  const sharedComps = competitors
    .filter((c) => c.shared)
    .sort((a, b) => (b.brand?.volume ?? 0) - (a.brand?.volume ?? 0));
  const trend = data?.trend;
  const withAds = competitors.filter((c) => c.ads.length > 0);
  const [adsFor, setAdsFor] = useState<string>('');
  const selectedAds = withAds.find((c) => c.domain === adsFor) ?? withAds[0];

  const trendOption = useMemo<EChartsOption>(() => {
    const t = chartTokens();
    const series = (trend?.series ?? []).filter((s) => s.data.some((v) => v != null && v > 0));
    return {
      ...baseChart(t),
      tooltip: { trigger: 'axis' },
      legend: { type: 'scroll', top: 0 },
      grid: { left: 60, right: 16, top: 40, bottom: 28 },
      xAxis: { type: 'category', data: trend?.labels ?? [], ...axisStyle(t) },
      yAxis: { type: 'value', name: '₽/день', ...axisStyle(t) },
      series: series.map((s, i) => ({
        name: s.name,
        type: 'line',
        smooth: true,
        showSymbol: false,
        connectNulls: true,
        data: s.data,
        lineStyle: { width: 2 },
        color: PALETTE[i % PALETTE.length],
      })),
    };
  }, [trend]);

  const trendReady = (trend?.labels?.length ?? 0) >= 2;

  return (
    <div className="col gap20" style={{ maxWidth: 1200 }}>
      <div className="row spread wrap" style={{ gap: 12 }}>
        <span className="muted" style={{ fontSize: 13 }}>
          Снапшот: <span className="mono">{dt(data?.generatedAt)}</span>
        </span>
        <Button
          variant="outline"
          size="sm"
          icon="Loader"
          onClick={() => refresh.mutate()}
          disabled={refresh.isPending}
        >
          {refresh.isPending ? 'Собираю SpyWords…' : 'Обновить'}
        </Button>
      </div>

      {refresh.isError && <ErrorBlock text={refresh.error?.message} />}
      {isLoading && <LoadingBlock label="Загружаю разведку…" />}
      {isError && <ErrorBlock text={error?.message} onRetry={() => void refetch()} />}

      {!isLoading && data && !data.configured && (
        <div className="card">
          <p className="muted" style={{ margin: 0 }}>
            SpyWords не настроен (нет SPYWORDS_LOGIN / SPYWORDS_TOKEN). Добавьте секрет — и нажмите «Обновить».
          </p>
        </div>
      )}

      {!isLoading && data?.configured && (
        <>
          {competitors.length === 0 ? (
            <div className="card">
              <p className="muted" style={{ margin: 0 }}>
                Снапшота ещё нет. Нажмите «Обновить», чтобы собрать данные по конкурентам.
              </p>
            </div>
          ) : (
            <>
              {/* Таблица */}
              <section className="card">
                <h3 style={{ marginTop: 0 }}>Конкуренты в Директе (Яндекс)</h3>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Конкурент</th>
                      <th>Категория</th>
                      <th style={{ textAlign: 'right' }}>Расход/день</th>
                      <th style={{ textAlign: 'right' }}>Платных ключей</th>
                      <th style={{ textAlign: 'right' }}>Платный трафик</th>
                      <th style={{ textAlign: 'right' }}>Ср. поз.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {domainComps.map((c) => (
                      <CompetitorRow key={c.domain} c={c} />
                    ))}
                  </tbody>
                </table>
                <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
                  Расход — оценка SpyWords. <Badge tone="b-warning">не таргетим</Badge> — по правилу заказчика
                  (Битрикс24) бидим только разведка, без замещения.
                </p>
              </section>

              {sharedComps.length > 0 && (
                <section className="card">
                  <h3 style={{ marginTop: 0 }}>Конкуренты на общем домене (по бренд-запросу)</h3>
                  <p className="muted" style={{ marginTop: 0, fontSize: 12.5 }}>
                    У них нет своего рекламного домена (живут в экосистеме — kontur.ru / yandex.ru / vk.com),
                    расход по контексту не выделить. Меряем по спросу на бренд.
                  </p>
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>Конкурент</th>
                        <th>Категория</th>
                        <th style={{ textAlign: 'right' }}>Бренд-спрос/мес</th>
                        <th style={{ textAlign: 'right' }}>Широкий</th>
                        <th style={{ textAlign: 'right' }}>Рекламодателей</th>
                        <th style={{ textAlign: 'right' }}>Ср. CPC</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sharedComps.map((c) => (
                        <tr key={c.domain}>
                          <td>
                            <div className="col" style={{ gap: 2 }}>
                              <span style={{ fontWeight: 600 }}>{c.name}</span>
                              <span className="muted mono" style={{ fontSize: 11.5 }}>
                                {c.brand?.keyword ?? c.domain}
                              </span>
                            </div>
                          </td>
                          <td>
                            <span className="muted" style={{ fontSize: 12.5 }}>{c.category}</span>
                          </td>
                          <td className="tnum" style={{ textAlign: 'right' }}>{c.brand ? num(c.brand.volume) : '—'}</td>
                          <td className="tnum" style={{ textAlign: 'right' }}>{c.brand ? num(c.brand.volumeTot) : '—'}</td>
                          <td className="tnum" style={{ textAlign: 'right' }}>{c.brand ? num(c.brand.advTot) : '—'}</td>
                          <td className="tnum" style={{ textAlign: 'right' }}>{c.brand ? rub(c.brand.avgCpc) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )}

              {/* Тренд расхода */}
              <section className="card">
                <h3 style={{ marginTop: 0 }}>Динамика расхода конкурентов</h3>
                {trendReady ? (
                  <BaseChart option={trendOption} height={320} ariaLabel="Динамика расхода конкурентов" />
                ) : (
                  <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                    История копится — тренд появится после нескольких суточных снапшотов (cron 08:30 МСК).
                  </p>
                )}
              </section>

              {/* Объявления конкурентов */}
              {withAds.length > 0 && (
                <section className="card">
                  <div className="row spread wrap" style={{ marginBottom: 12, gap: 8 }}>
                    <h3 style={{ margin: 0 }}>Чем отстраиваются (их объявления)</h3>
                    <select
                      className="input"
                      style={{ maxWidth: 240 }}
                      value={selectedAds?.domain ?? ''}
                      onChange={(e) => setAdsFor(e.target.value)}
                    >
                      {withAds.map((c) => (
                        <option key={c.domain} value={c.domain}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col" style={{ gap: 10 }}>
                    {selectedAds?.ads.map((a, i) => (
                      <div key={i} className="card" style={{ background: 'var(--surface-2, #F8FAFC)', padding: '10px 12px' }}>
                        <div style={{ fontSize: 13.5, lineHeight: 1.5 }}>{a.adCopy}</div>
                        <div className="muted mono" style={{ fontSize: 11.5, marginTop: 6 }}>
                          ключ: {a.keyword || '—'} · vol {num(a.volume)} · CPC {rub(a.cpc)}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Поиск ключей замещения */}
              <KeywordFinder />
            </>
          )}
        </>
      )}
    </div>
  );
}

function CompetitorRow({ c }: { c: CompetitorEntry }) {
  const o = c.overview;
  return (
    <tr>
      <td>
        <div className="col" style={{ gap: 2 }}>
          <span style={{ fontWeight: 600 }}>{c.name}</span>
          <span className="muted mono" style={{ fontSize: 11.5 }}>{c.domain}</span>
        </div>
      </td>
      <td>
        {c.conquest ? (
          <span className="muted" style={{ fontSize: 12.5 }}>{c.category}</span>
        ) : (
          <Badge tone="b-warning">не таргетим</Badge>
        )}
      </td>
      <td className="tnum" style={{ textAlign: 'right' }}>{o ? rub(o.adSpend) : '—'}</td>
      <td className="tnum" style={{ textAlign: 'right' }}>{o ? num(o.keysAd) : '—'}</td>
      <td className="tnum" style={{ textAlign: 'right' }}>{o ? num(o.adTraf) : '—'}</td>
      <td className="tnum" style={{ textAlign: 'right' }}>{o && o.avgPos ? o.avgPos : '—'}</td>
    </tr>
  );
}

function KeywordFinder() {
  const [word, setWord] = useState('');
  const lookup = useKeywordLookup();
  const res = lookup.data;

  const submit = () => {
    const w = word.trim();
    if (w) lookup.mutate(w);
  };

  return (
    <section className="card">
      <h3 style={{ marginTop: 0 }}>Поиск ключей замещения</h3>
      <p className="muted" style={{ marginTop: 0, fontSize: 12.5 }}>
        Спрос, CPC и кто уже бидует по фразе. Напр.: «телеграм заблокировали», «корпоративный мессенджер».
      </p>
      <div className="row" style={{ gap: 8, marginBottom: 12 }}>
        <input
          className="input"
          placeholder="фраза замещения…"
          value={word}
          onChange={(e) => setWord(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          style={{ maxWidth: 360 }}
        />
        <Button variant="primary" size="sm" onClick={submit} disabled={lookup.isPending || !word.trim()}>
          {lookup.isPending ? 'Ищу…' : 'Проверить'}
        </Button>
      </div>

      {lookup.isError && <ErrorBlock text={lookup.error?.message} />}

      {res?.overview && (
        <div className="stat-grid" style={{ marginBottom: 12 }}>
          <MiniStat label="Спрос/мес (точн.)" value={num(res.overview.volume)} />
          <MiniStat label="Спрос/мес (широкий)" value={num(res.overview.volumeTot)} />
          <MiniStat label="Рекламодателей" value={num(res.overview.advTot)} />
          <MiniStat label="Ср. CPC" value={rub(res.overview.avgCpc)} />
        </div>
      )}
      {res && !res.overview && res.word && (
        <p className="muted" style={{ fontSize: 13 }}>Фразы «{res.word}» нет в индексе SpyWords (низкочастотная).</p>
      )}

      {(res?.bidders?.length ?? 0) > 0 && (
        <table className="tbl">
          <thead>
            <tr>
              <th>Домен</th>
              <th style={{ textAlign: 'right' }}>Ср. поз.</th>
              <th>Объявление</th>
            </tr>
          </thead>
          <tbody>
            {res!.bidders!.map((b, i) => (
              <tr key={i}>
                <td className="mono" style={{ whiteSpace: 'nowrap' }}>{b.domain}</td>
                <td className="tnum" style={{ textAlign: 'right' }}>{b.avgPos || '—'}</td>
                <td style={{ fontSize: 12.5 }}>{b.adCopy}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="stat">
      <div className="stat__label">{label}</div>
      <div className="stat__value" style={{ fontSize: 22 }}>{value}</div>
    </div>
  );
}
